import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;

  const goTo = (index: number) => setActive((index + movies.length) % movies.length);

  return (
    <section className="relative w-full overflow-hidden bg-background-50">
      <div className="relative h-[520px] w-full md:h-[620px]">
        {movies.map((movie, index) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={movie.backdrop}
              alt={`${movie.title} 主视觉 - 午夜场精选电影`}
              title={`${movie.title} - 午夜电影推荐`}
              className={`h-full w-full object-cover object-top ${
                index === active ? "animate-kenburns" : ""
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/40 to-transparent" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] items-center px-4 md:px-6 lg:px-10">
          <div key={active} className="animate-fade-up max-w-2xl">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-primary-500/10 px-3 py-1 text-xs font-semibold text-primary-300">
              <i className="ri-moon-clear-line"></i>
              本周午夜精选 · 第 {active + 1} 部
            </span>
            <h1 className="mb-3 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground-50 md:text-5xl">
              {movies[active].title}
            </h1>
            <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-400">
              <span className="flex items-center gap-1 font-semibold text-primary-400">
                <i className="ri-star-fill"></i>
                {movies[active].rating.toFixed(1)}
              </span>
              <span>{movies[active].year}</span>
              <span>{movies[active].region}</span>
              <span>{movies[active].duration} 分钟</span>
              <span className="hidden sm:inline">{movies[active].genres.join(" · ")}</span>
            </div>
            <p className="mb-7 max-w-xl text-sm leading-relaxed text-foreground-300 md:text-base">
              {movies[active].summary}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/movie/${movies[active].id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-line text-lg"></i>
                查看影片详情
              </Link>
              <Link
                to={`/category?genre=${encodeURIComponent(movies[active].genres[0])}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50/40 px-5 py-3 text-sm font-semibold text-foreground-200 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-300"
              >
                <i className="ri-price-tag-3-line"></i>
                {movies[active].genres[0]}类午夜片
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
          {movies.map((movie, index) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到 ${movie.title}`}
              onClick={() => goTo(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === active ? "w-8 bg-primary-500" : "w-4 bg-foreground-700 hover:bg-foreground-500"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          aria-label="上一部"
          onClick={() => goTo(active - 1)}
          className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-300 bg-background-50/60 text-foreground-100 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-300 md:left-6"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一部"
          onClick={() => goTo(active + 1)}
          className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-background-300 bg-background-50/60 text-foreground-100 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-300 md:right-6"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}